let reportQueue: Array<{ message: string; stack?: string; url: string; type: string; component?: string }> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const CHUNK_LOAD_ERROR_PATTERNS = [
  "Loading chunk",
  "dynamically imported module",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
];

function isChunkLoadErrorMessage(message: string) {
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function flush() {
  if (reportQueue.length === 0) return;
  const batch = reportQueue.splice(0, reportQueue.length);
  for (const entry of batch) {
    try {
      const body = JSON.stringify(entry);
      if (navigator.sendBeacon) {
        navigator.sendBeacon("/api/client-errors", new Blob([body], { type: "application/json" }));
      } else {
        fetch("/api/client-errors", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {}
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, 2000);
}

const seen = new Set<string>();

function report(message: string, stack?: string, type = "error", component?: string) {
  const key = `${type}:${message.substring(0, 100)}`;
  if (seen.has(key)) return;
  seen.add(key);
  if (seen.size > 200) seen.clear();

  reportQueue.push({
    message: message.substring(0, 500),
    stack: stack?.substring(0, 1000),
    url: window.location.href,
    type,
    component,
  });

  if (reportQueue.length >= 5) {
    flush();
  } else {
    scheduleFlush();
  }
}

export function initErrorReporter() {
  window.addEventListener("error", (event) => {
    if (!event.error) return;
    const message = event.error?.message || event.message || "Unknown error";
    if (isChunkLoadErrorMessage(message)) return;
    report(
      message,
      event.error?.stack,
      "uncaught"
    );
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    if (isChunkLoadErrorMessage(message)) return;
    report(message, stack, "unhandled-rejection");
  });

  window.addEventListener("beforeunload", () => {
    flush();
  });
}

export function reportComponentError(error: Error, componentName?: string) {
  report(error.message, error.stack, "react-component", componentName);
}
